import { useMemo, type ReactNode } from "react"
import { ClockCircleOutlined, DownOutlined } from "@ant-design/icons"
import { App, Button, Flex, Popover, Segmented, Select, Tooltip } from "antd"
import type { FlexProps } from "antd"
import { Text } from "~/components/Text"
import classNames from "classnames"
import {
  DEFAULT_VIDEO_CHANNEL_RESOLUTIONS,
  VIDEO_CHANNEL_RESOLUTIONS,
  getSafeVideoDuration,
  getSafeVideoRatio,
  getSafeVideoResolution,
  getVideoDurationOptions,
  getVideoRatioOptions,
  getVideoResolutionOptions,
  RATIOS,
} from "~/common/constants"
import { formatDurationSeconds } from "~/common/utils"
import { VideoModelSelect } from "~/components/VideoModelSelect"
import { type VideoModelDisabledReasons } from "~/common/video/videoModels"
import styles from "./index.css"

const defaultRatioRectMaxSize = 30
const compactRatioRectMaxSize = 18

const getRatioRectSize = (ratio: string, maxSize = defaultRatioRectMaxSize) => {
  const [widthRatio, heightRatio] = ratio.split(":").map(Number)

  if (!widthRatio || !heightRatio) {
    return {
      height: maxSize,
      width: maxSize,
    }
  }

  const aspectRatio = widthRatio / heightRatio

  if (aspectRatio > 1) {
    return {
      height: maxSize / aspectRatio,
      width: maxSize,
    }
  }

  return {
    height: maxSize,
    width: maxSize * aspectRatio,
  }
}

type RatioRectProps = {
  compact?: boolean
  ratio: string
}

const RatioRect = ({ compact = false, ratio }: RatioRectProps) => {
  const rectSize = useMemo(() => {
    return getRatioRectSize(
      ratio,
      compact ? compactRatioRectMaxSize : defaultRatioRectMaxSize,
    )
  }, [compact, ratio])

  return (
    <Flex
      className={classNames(
        styles.ratioRect,
        compact && styles.ratioRectCompact,
      )}
      align="center"
      justify="center"
      vertical
    >
      <span
        className={styles.ratioShape}
        style={rectSize}
      />
      {compact ? null : (
        <Text
          size="s"
          color="inherit"
        >
          {ratio}
        </Text>
      )}
    </Flex>
  )
}

type VideoParameterSelectProps = {
  wrap?: FlexProps["wrap"]
  actions?: ReactNode
  hideOptions?: (keyof VideoParameterValue)[]
  durationReadOnly?: boolean
  modelDisabledReasons?: VideoModelDisabledReasons
  value: VideoParameterValue
  onChange: (value: VideoParameterValue) => void
}

export type VideoParameterValue = {
  duration?: number
  model: API.Task.CreateVideoChannel
  ratio: API.VideoRatio
  resolution: API.VideoResolution
}

export const VideoParameterSelect = (props: VideoParameterSelectProps) => {
  const { message } = App.useApp()
  const {
    hideOptions = [],
    durationReadOnly = false,
    onChange,
    value,
    wrap = true,
  } = props
  const { duration, model, ratio, resolution } = value
  const availableResolutions =
    VIDEO_CHANNEL_RESOLUTIONS[model] ?? DEFAULT_VIDEO_CHANNEL_RESOLUTIONS
  const safeResolution = getSafeVideoResolution(model, resolution)
  const ratioOptions = getVideoRatioOptions(model)
  const resolutionOptions = getVideoResolutionOptions(model)
  const durationOptions = getVideoDurationOptions(model)
  const shouldShowDuration = !!duration && !hideOptions.includes("duration")
  const showRatio = !hideOptions.includes("ratio")
  const showResolution = !hideOptions.includes("resolution")

  const updateValue = (nextValue: Partial<VideoParameterValue>) => {
    onChange({
      ...value,
      ...nextValue,
    })
  }

  const updateModel = (nextModel: API.Task.CreateVideoChannel) => {
    if (
      (hideOptions.includes("duration") &&
        duration !== undefined &&
        !getVideoDurationOptions(nextModel).includes(duration)) ||
      (hideOptions.includes("ratio") &&
        !getVideoRatioOptions(nextModel).includes(ratio)) ||
      (hideOptions.includes("resolution") &&
        !VIDEO_CHANNEL_RESOLUTIONS[nextModel]?.includes(resolution))
    ) {
      void message.warning("当前模型不支持模板固定参数，请选择其他模型")
      return
    }
    const error = props.modelDisabledReasons?.[nextModel]
    if (error) {
      void message.warning(error)
      return
    }
    const nextRatio = getSafeVideoRatio(nextModel, ratio)
    const nextResolution = getSafeVideoResolution(nextModel, resolution)

    onChange({
      ...value,
      duration:
        hideOptions.includes("duration") || typeof duration !== "number"
          ? duration
          : getSafeVideoDuration(nextModel, duration),
      model: nextModel,
      ratio: nextRatio,
      resolution: nextResolution,
    })

    if (resolution && nextResolution !== resolution) {
      void message.info(
        `当前模型不支持 ${resolution.toUpperCase()}，已切换为 ${nextResolution.toUpperCase()}`,
      )
    }
  }

  const updateRatio = (nextRatio: API.VideoRatio) => {
    updateValue({ ratio: nextRatio })
  }

  const updateResolution = (nextResolution: API.VideoResolution) => {
    updateValue({ resolution: nextResolution })
  }

  const parameterPopover = (
    <Flex
      gap={14}
      vertical
    >
      {showRatio && (
        <Flex
          gap={8}
          vertical
        >
          <Text>选择比例</Text>
          <Segmented
            className={styles.segmented}
            options={RATIOS.map((item) => ({
              disabled: !ratioOptions.includes(item),
              label: <RatioRect ratio={item} />,
              value: item,
            }))}
            value={ratio}
            onChange={(nextRatio) => updateRatio(nextRatio as API.VideoRatio)}
          />
        </Flex>
      )}

      {showResolution && (
        <Flex
          gap={8}
          vertical
        >
          <Text>选择分辨率</Text>
          <Segmented
            className={styles.segmented}
            options={resolutionOptions.map((item) => ({
              disabled: !availableResolutions.includes(item),
              label: item.toUpperCase(),
              value: item,
            }))}
            value={resolution}
            onChange={(nextResolution) =>
              updateResolution(nextResolution as API.VideoResolution)
            }
          />
        </Flex>
      )}
    </Flex>
  )
  return (
    <Flex
      className={styles.parameterSelect}
      align="center"
      gap={4}
      wrap={wrap}
    >
      {props.actions || !hideOptions.includes("model") ? (
        <Flex
          align="center"
          gap={8}
        >
          {props.actions}
          {!hideOptions.includes("model") && (
            <VideoModelSelect
              disabledReasons={props.modelDisabledReasons}
              value={model}
              onChange={updateModel}
            />
          )}
        </Flex>
      ) : null}
      {(showRatio || showResolution) && (
        <Popover
          arrow={false}
          content={parameterPopover}
          placement="bottomLeft"
          trigger="click"
        >
          <Button shape="round">
            {showRatio && (
              <RatioRect
                ratio={ratio}
                compact
              />
            )}
            {[
              showRatio ? ratio : "",
              showResolution ? safeResolution.toUpperCase() : "",
            ]
              .filter(Boolean)
              .join(" · ")}
            <DownOutlined className={styles.downIcon} />
          </Button>
        </Popover>
      )}
      {shouldShowDuration ? (
        durationReadOnly ? (
          <Tooltip title="根据分镜时长自动计算">
            <Flex
              component="span"
              align="center"
              gap={8}
              className={styles.durationDisplay}
              aria-label={`生成时长 ${formatDurationSeconds(duration)}，根据分镜自动计算`}
            >
              <ClockCircleOutlined />
              <Text>{formatDurationSeconds(duration)}</Text>
            </Flex>
          </Tooltip>
        ) : (
          <Select
            className={styles.durationSelect}
            options={durationOptions.map((item) => ({
              label: formatDurationSeconds(item),
              value: item,
            }))}
            prefix={<ClockCircleOutlined />}
            value={duration}
            onChange={(nextDuration) => updateValue({ duration: nextDuration })}
          />
        )
      ) : null}
    </Flex>
  )
}
